<template>
  <Notification v-if="feed?.integration" :item="item">
    <template #logo>
      <BittsSvg :svg="feed.integration.type + 'Icon'" />
    </template>
    <template #message> Data Ready </template>
    <template #description>
      Your {{ feed?.integration.friendly_name }} data has finished processing
    </template>
    <template v-if="canCreatePopulation" #action>
      <BittsButton
        text="Create Population"
        type="neutral"
        variant="outline"
        size="x-small"
        @click="createPopulation"
      />
    </template>
    <template #icon>
      <FontAwesomeIcon
        :icon="['fak', 'company']"
        :style="{ height: '12px', width: '12px', color: 'currentColor' }"
        class="text-neutral-accent ml-4"
      />
    </template>
  </Notification>
</template>
<script setup lang="ts">
import { BittsButton, BittsSvg } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import Notification from '@/components/notifications/Notification.vue';

import useNotifications from '@/composables/useNotifications';
import {
  useBillingStore,
  useFeedsStore,
  useNotificationsStore,
  usePopulationsStore,
} from '@/stores';
import { Notification as NotificationType } from '@/types/notifications';
import {
  CUSTOMERS_STANDARD_TYPE,
  OPEN_OPPS_STANDARD_TYPE,
  PROSPECTS_STANDARD_TYPE,
} from '@/types/populations';

const { item } = defineProps<{ item: NotificationType }>();

const emit = defineEmits<(e: 'action-clicked', action: string) => void>();

const { parseFeedIdFromFeedProcessedEvent } = useNotifications();

const feedsStore = useFeedsStore();
const notificationsStore = useNotificationsStore();
const populationsStore = usePopulationsStore();
const billingStore = useBillingStore();
const { customPopulationLimit } = storeToRefs(billingStore);

const feed = computed(() => {
  return feedsStore.getFeedById(parseFeedIdFromFeedProcessedEvent(item));
});

const existingStandardPopulations = computed(() => {
  return populationsStore.populations.filter((pop) => pop.standard_type);
});

const missingStandardPopulation = computed(() => {
  const existingTypes = existingStandardPopulations.value.map(
    (pop) => pop.standard_type,
  );
  if (!existingTypes.includes(CUSTOMERS_STANDARD_TYPE)) {
    return CUSTOMERS_STANDARD_TYPE;
  }
  if (!existingTypes.includes(OPEN_OPPS_STANDARD_TYPE)) {
    return OPEN_OPPS_STANDARD_TYPE;
  }
  if (!existingTypes.includes(PROSPECTS_STANDARD_TYPE)) {
    return PROSPECTS_STANDARD_TYPE;
  }
  return null;
});

const canCreatePopulation = computed(
  () => !!missingStandardPopulation.value || !!customPopulationLimit.value,
);

const router = useRouter();

async function createPopulation() {
  emit('action-clicked', 'Create Population');
  await notificationsStore.markRead({ id: item.id, isRead: true });

  const standardTypeToCreate = missingStandardPopulation.value;

  if (standardTypeToCreate) {
    await router.push({
      name: 'create_population',
      query: {
        standardType: standardTypeToCreate,
      },
    });
    return;
  }

  await router.push({ name: 'create_population' });
}
</script>
